import React, {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import useSWR from 'swr';
import {
    Box,
    Button,
    Chip,
    Container,
    Divider,
    Grid, Stack,
    TextField,
    Typography
} from '@mui/material';
import {getFetcher, putFetcher} from "../../utils/api/fetchers";
import Loading from "../../components/status/Loading";
import ErrorMessage from "../../components/status/ErrorMessage";
import {useAuth} from "../../utils/hooks/useAuth";
import {UserAccountDetails, userAccountDetailsSchema} from "../../schemas/user/userAccountDetailsSchemas";
import UserRole from "../../schemas/user/roles";
import usePasswordChangeDialog from "../../components/dialog/PasswordChangeDialog";
import {useNotification} from "../../utils/hooks/useNotification";
import {zodResolver} from "@hookform/resolvers/zod";
import RolesSelector from "../../components/input/RolesSelector";
import {updateRoles} from "../../utils/users/updateRoles";

const UserProfile: React.FC = () => {
    const {user} = useAuth();
    const {showNotification} = useNotification();
    const {data, error, isLoading, mutate} = useSWR<UserAccountDetails>(`/users/${user?.id}/details`, getFetcher);
    const {register, handleSubmit, reset, formState: {errors, isDirty}} = useForm<UserAccountDetails>({
        defaultValues: data,
        resolver: zodResolver(userAccountDetailsSchema),
    });
    const [readOnly, setReadOnly] = useState<boolean>(true);
    const [roles, setRoles] = useState<UserRole[]>([]);
    const {openDialog, PasswordChangeDialogComponent} = usePasswordChangeDialog();

    useEffect(() => {
        if (data) {
            reset(data);
            setRoles(data.roles);
        }
    }, [data, reset]);

    if (isLoading || !data || !user) return <Loading/>;
    if (error) return <ErrorMessage/>;

    const onSubmit = async (formData: UserAccountDetails) => {
        const rolesNotChanged = await updateRoles(formData, roles, showNotification);
        if (!isDirty && rolesNotChanged) {
            showNotification("Nebyly provedeny žádné změny", "info");
            return;
        }
        putFetcher(`/users/${user?.id}`, formData)
            .then(() => {
                mutate();
                showNotification("Profil byl úspěšně uložen", "success");
                setReadOnly(true);
            })
            .catch((error) => {
                if (error?.status === 409) {
                    showNotification("Uživatel s tímto emaile již existuje", "error");
                } else {
                    showNotification("Nepodařilo se uložit změny", "error");
                }
            });
    };

    return (
        <Container component="main" maxWidth={false}>
            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{
                mt: 3,
                boxShadow: 5,
                padding: {xs: 2, sm: 6},
                borderRadius: 2,
                backgroundColor: 'white',
            }}>
                <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} sm={6}>
                        <Typography variant="h4" component='h1'>
                            Profil uživatele
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} style={{textAlign: 'right'}}>
                        <Typography variant="h4">
                            {data.firstName} {data.lastName} – {data.username}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Divider/>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="standard"
                            margin="normal"
                            label="Jméno"
                            required
                            fullWidth
                            {...register('firstName')}
                            autoComplete="given-name"
                            error={!!errors.firstName}
                            helperText={errors.firstName?.message}
                            InputProps={{
                                readOnly: readOnly,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="standard"
                            margin="normal"
                            label="Příjmení"
                            required
                            fullWidth
                            {...register('lastName')}
                            autoComplete="family-name"
                            error={!!errors.lastName}
                            helperText={errors.lastName?.message}
                            InputProps={{
                                readOnly: readOnly,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="standard"
                            margin="normal"
                            label="Telefon"
                            required
                            fullWidth
                            {...register('phoneNumber')}
                            autoComplete="tel"
                            error={!!errors.phoneNumber}
                            helperText={errors.phoneNumber?.message}
                            InputProps={{
                                readOnly: readOnly,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            variant="standard"
                            margin="normal"
                            label="Email"
                            required
                            fullWidth
                            {...register('email')}
                            autoComplete="email"
                            error={!!errors.email}
                            helperText={errors.email?.message}
                            InputProps={{
                                readOnly: readOnly,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <RolesSelector selectedRoles={roles} setRoles={setRoles} readOnly={readOnly}/>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography>
                            Stav účtu:
                        </Typography>
                        <Chip
                            label={data.enabled ? 'Aktivní' : 'Neaktivní'}
                            color={data.enabled ? 'success' : 'error'}
                            sx={{margin: '5px'}}
                        />
                    </Grid>
                    <Grid item xs={12} sx={{textAlign: 'right'}}>
                        <Stack direction={{xs: 'column', sm: 'row'}} spacing={2} justifyContent="flex-end">
                            {readOnly ? (
                                <Button variant="contained" sx={{px: 5}}
                                        onClick={() => {
                                            setReadOnly(false);
                                        }}
                                        color='primary'>
                                    Upravit
                                </Button>
                            ) : (
                                <>
                                    <Button variant="contained" sx={{px: 2}}
                                            onClick={() => {
                                                setReadOnly(true);
                                                reset(data);
                                                setRoles(data.roles || []);
                                            }}
                                            color='error'>
                                        Zrušit změny
                                    </Button>
                                    <Button type="submit" variant="contained" color="primary" sx={{px: 5}}
                                            disabled={readOnly} style={{visibility: readOnly ? 'hidden' : 'visible'}}>
                                        Uložit
                                    </Button>
                                </>
                            )}
                            <Button variant="outlined" sx={{px: 5}}
                                    onClick={openDialog}>
                                Změnit heslo
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </Box>
            <PasswordChangeDialogComponent/>
        </Container>
    )
        ;
};

export default UserProfile;